// ABOUTME: Parent-side Reset control — records a Reset for a child and hands off to the crossing (Issue #2012).
// ABOUTME: Disabled while a press is in flight so one press starts exactly one crossing.

import React, { useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { KalaIcon } from '../../utils/behaviorUtils';
import { StyledActionButton, mobileIconOnlySx } from '../shared/StyledActionButton';
import { BilingualLabel } from '../BilingualLabel';
import BehaviorCountBadge from '../BehaviorCountBadge';
import { useEffectiveLabelLanguageMode } from '../../hooks/useEffectiveLabelLanguageMode';
import naluV1Theme from '../../theme/theme.nalu-v1';
import { naluV1TallyLabelText } from '../../theme/tokens';

interface ParentResetButtonProps {
  child: { childId: number | string; name?: string };
  count: number;
  // Must handle its own errors: the button only tracks whether a press is still in flight.
  onReset: (childId: number | string) => Promise<void>;
}

const ParentResetButton: React.FC<ParentResetButtonProps> = ({ child, count, onReset }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const labelMode = useEffectiveLabelLanguageMode();

  const handleClick = async () => {
    setIsSubmitting(true);
    try {
      await onReset(child.childId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNaluV1 = theme.logoSrc === naluV1Theme.logoSrc;

  const button = (
    <BehaviorCountBadge count={count} color="primary">
      <StyledActionButton
        data-testid="parent-reset-button"
        variant="outlined"
        disabled={isSubmitting}
        onClick={handleClick}
        startIcon={<KalaIcon />}
        aria-label={`Record a Reset for ${child.name ?? 'this child'}`}
        sx={isMobile ? mobileIconOnlySx(isNaluV1) : undefined}
      >
        {isMobile ? null : <BilingualLabel term="piko" english="Reset" mode={labelMode ?? 'english-lead'} />}
      </StyledActionButton>
    </BehaviorCountBadge>
  );

  if (!isMobile || !isNaluV1) {
    return button;
  }

  // Phone-width nalu-v1 tiles carry a caption beneath, like the tally buttons; it may wrap inside the tile width.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      {button}
      <Box
        sx={{
          fontSize: '13px',
          fontWeight: 700,
          color: naluV1TallyLabelText,
          whiteSpace: { xs: 'normal', sm: 'nowrap' },
          textAlign: 'center',
          maxWidth: { xs: 60, sm: 'none' },
        }}
      >
        <BilingualLabel term="piko" english="Reset" mode={labelMode ?? 'english-lead'} />
      </Box>
    </Box>
  );
};

export default ParentResetButton;
